import i18n from 'config/i18n/i18n';
import { OptionsObject, useSnackbar, VariantType, WithSnackbarProps } from 'notistack';

/**
 * Mutable static internal reference holding the current instance configuration of Notistack.
 * Allows triggering snackbars anywhere across the codebase outside standard React component contexts.
 */
let useSnackbarRef: WithSnackbarProps;

/**
 * Global Bridge Component required to be rendered once within the React Application Component Tree.
 * Captures the initialized context from the parent `<SnackbarProvider>` and assigns it to the static engine.
 *
 * @example
 * ```tsx
 * import { SnackbarProvider } from 'notistack';
 * import { SnackbarUtilsConfigurator } from 'utils/toast';
 *
 * export const App = () => (
 *   <SnackbarProvider maxSnack={3}>
 *     <SnackbarUtilsConfigurator />
 *     <MainRouter />
 *   </SnackbarProvider>
 * );
 * ```
 */
export const SnackbarUtilsConfigurator: React.FC = () => {
  useSnackbarRef = useSnackbar();
  return null;
};

/**
 * Structural settings payload for dynamic configuration of preformatted CRUD transactional messages.
 */
type CrudParam = string | { entityName?: string; gender?: 'm' | 'f' };

const parseCrudArgs = (param?: CrudParam, defaultGender: 'm' | 'f' = 'm') => {
  if (typeof param === 'string') {
    const normalizedName = param ? param.charAt(0).toUpperCase() + param.slice(1) : 'Elemento';
    return { entityName: normalizedName, gender: defaultGender };
  }
  return {
    entityName: param?.entityName ?? 'Elemento',
    gender: param?.gender ?? defaultGender
  };
};

/**
 * Isolated core invocation middleware responsible for pushing payload entries into the Notistack enqueue loop.
 * Safeguards execution states against variable hoisting, Temporal Dead Zones (TDZ), and object context (`this`) coupling.
 *
 * @param msg - The core notification text string content to render inside the banner workspace.
 * @param variant - Visual color-coded semantic type classifying importance levels (success, error, warning, info, default).
 * @param options - Advanced override parameters mapped down to raw MUI/Notistack Snackbar customization states.
 */
const triggerToast = (
  msg: string,
  variant: VariantType = 'default',
  options: OptionsObject = {}
) => {
  if (useSnackbarRef) {
    useSnackbarRef.enqueueSnackbar(msg, {
      variant,
      preventDuplicate: true,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      ...options
    });
  } else {
    console.warn("SnackbarUtilsConfigurator non è montato nell'App Tree.");
  }
};

/**
 * Global API Client & State Notification Utility.
 * Streamlines atomic event feedback pipelines, automated backend CRUD messaging schemes, and HTTP Network status evaluation.
 */
export default {
  /**
   * Dispatches a semantic success notification (typically rendered as a green backdrop layout).
   *
   * @param msg - Core statement highlighting successful operation completion.
   * @param options - Native Notistack structural properties to override layout rules on-the-fly.
   * @example toast.success("File uploaded successfully.");
   */
  success(msg: string, options?: OptionsObject) {
    triggerToast(msg, 'success', options);
  },

  /**
   * Dispatches a warning notification alerting operators to non-blocking system states (typically yellow/orange).
   *
   * @param msg - Content statement outlining potential risks, edge cases, or validation requirements.
   * @param options - Native Notistack structural properties to override layout rules on-the-fly.
   * @example toast.warning("Disk space running low (under 10%).");
   */
  warning(msg: string, options?: OptionsObject) {
    triggerToast(msg, 'warning', options);
  },

  /**
   * Dispatches an informational notification providing system-level process insights (typically blue).
   *
   * @param msg - Log trace descriptive statement detailing behind-the-scenes system progression.
   * @param options - Native Notistack structural properties to override layout rules on-the-fly.
   * @example toast.info("Sync engine execution loop initiated.");
   */
  info(msg: string, options?: OptionsObject) {
    triggerToast(msg, 'info', options);
  },

  /**
   * Dispatches a critical error notification demanding immediate administrative inspection (typically red).
   *
   * @param msg - Fault statement identifying broken integrations, network limits, or bad payloads.
   * @param options - Native Notistack structural properties to override layout rules on-the-fly.
   * @example toast.error("Database connection refused. Handshake timeout.");
   */
  error(msg: string, options?: OptionsObject) {
    triggerToast(msg, 'error', options);
  },

  /**
   * Dispatches a neutral-styled default toast lacking explicit color-coded semantic significance.
   *
   * @param msg - Text content body showing clean system statements.
   * @param options - Native Notistack structural properties to override layout rules on-the-fly.
   */
  default(msg: string, options?: OptionsObject) {
    triggerToast(msg, 'default', options);
  },

  /**
   * Enterprise CRUD-specialized feedback subsystem.
   * Automates standard formatting matrices for Create, Read, Update, and Delete actions across data entities.
   */
  crud: {
    /**
     * Announces the successful addition and persistence of a new record entry.
     *
     * @param options - Formatting directives outlining entity name parameters and grammatical overrides.
     * @example toast.crud.successCreate({ entityName: 'Attività', gender: 'f' }) -> "Attività creata con successo!"
     */
    successCreate(param?: CrudParam, gender?: 'm' | 'f') {
      const args = parseCrudArgs(param, gender);
      const msg = i18n.t(`actions.successfully.created_${args.gender}`, {
        entity: args.entityName
      });
      triggerToast(msg, 'success');
    },

    /**
     * Announces a network or validation failure aborting a record creation process.
     *
     * @param options - Formatting directives outlining target model boundaries.
     * @example toast.crud.errorCreate({ entityName: 'Utente' }) -> "Impossibile completare la creazione dell'utente. Riprova."
     */
    errorCreate(param?: CrudParam) {
      const args = parseCrudArgs(param);
      const msg = i18n.t('actions.errors.create', { entity: args.entityName.toLowerCase() });
      triggerToast(msg, 'error');
    },

    /**
     * Announces the successful payload serialization and commitment of updates on an existing entity instance.
     *
     * @param options - Formatting directives outlining tracking configuration.
     * @example toast.crud.successUpdate('Strumento') -> "Strumento aggiornato correttamente!"
     */
    successUpdate(param?: CrudParam, gender?: 'm' | 'f') {
      const args = parseCrudArgs(param, gender);
      const msg = i18n.t(`actions.successfully.updated_${args.gender}`, {
        entity: args.entityName
      });
      triggerToast(msg, 'success');
    },

    /**
     * Announces a database rollback or transmission fault occurring during a modification request loop.
     *
     * @param options - Formatting directives managing system metadata text injections.
     */
    errorUpdate(param?: CrudParam) {
      const args = parseCrudArgs(param);
      const msg = i18n.t('actions.errors.update', { entity: args.entityName.toLowerCase() });
      triggerToast(msg, 'error');
    },

    /**
     * Announces the successful deletion or logical soft-purge of a target asset record instance.
     *
     * @param options - Structural options resolving model attributes string tracking.
     */
    successDelete(param?: CrudParam, gender?: 'm' | 'f') {
      const args = parseCrudArgs(param, gender);
      const msg = i18n.t(`actions.successfully.deleted_${args.gender}`, {
        entity: args.entityName
      });
      triggerToast(msg, 'success');
    },

    /**
     * Announces constraint failures or credential checks blocking a record destruction event loop.
     *
     * @param options - Structural options resolving naming context.
     */
    errorDelete(param?: CrudParam) {
      const args = parseCrudArgs(param);
      const msg = i18n.t('actions.errors.delete', { entity: args.entityName.toLowerCase() });
      triggerToast(msg, 'error');
    },

    /**
     * Announces the successful lifecycle signature validation, workflow approval, or technical audit verification check.
     *
     * @param options - Structural tracking variables managing localized linguistic string outputs.
     */
    successValidation(param?: CrudParam, gender?: 'm' | 'f') {
      const args = parseCrudArgs(param, gender);
      const msg = i18n.t(`actions.successfully.validated_${args.gender}`, {
        entity: args.entityName
      });
      triggerToast(msg, 'success');
    },

    /**
     * Announces the successful multi-row execution block results following batch processing commands inside a DataGrid.
     *
     * @param count - Total number of dataset row records modified or purged.
     * @param options - Structural entity naming scope properties.
     * @example toast.crud.successBulkDelete(14, { entityName: 'attività' }) -> "Eliminati con successo 14 attività."
     */
    successBulkDelete(count: number, param?: CrudParam) {
      const args = parseCrudArgs(param);
      const entityName =
        args.entityName !== 'Elemento' ? args.entityName.toLowerCase() : i18n.t('global.elements');
      const msg = i18n.t('actions.successfully.bulk_deleted', { count, entity: entityName });
      triggerToast(msg, 'success');
    }
  },

  /**
   * Global HTTP Response Error Interceptor & Middleware Parser.
   * Evaluates catch block structures and triggers clean, localized, and safe user-facing fallbacks.
   * Strictly hides internal backend execution messages from clients to preserve security bounds.
   *
   * @param error - The raw error artifact captured inside a try/catch processing sequence (typically AxiosError).
   * @param fallbackMessage - String literal indicating what to show if status code is unmatched or network is dead.
   */
  apiError(error: any, fallbackMessage?: string) {
    const status = error?.response?.status;
    const isDevelopment = process.env.NODE_ENV === 'development';
    let backendMessage = error?.response?.data?.detail || error?.response?.data?.message;
    let backendToUserMessage = '';

    if (error?.response?.data && typeof error.response.data === 'object' && !backendMessage) {
      const keys = Object.keys(error.response.data);
      if (keys.length > 0) {
        const firstFieldError = error.response.data[keys[0]];
        backendMessage = Array.isArray(firstFieldError)
          ? firstFieldError[0]
          : String(firstFieldError);
      }
    }

    if (error?.response?.data?.error) {
      backendToUserMessage = error.response.data.error;
      if (backendToUserMessage == null && error?.extra) {
        backendToUserMessage = error?.extra;
      }
      triggerToast(backendToUserMessage, 'error');
      return;
    }
    if (isDevelopment && backendMessage) {
      triggerToast(`[DEV ERROR] ${backendMessage}`, 'error');
      return;
    }
    const rawNetworkMessage = error?.message || 'Si è verificato un errore imprevisto.';
    const productionFallback =
      fallbackMessage || 'Si è verificato un errore di rete o di connessione.';

    switch (status) {
      case 400:
        triggerToast(
          rawNetworkMessage ? error?.respon : i18n.t('actions.api.errors.status_400'),
          'error'
        );
        break;
      case 401:
        triggerToast(
          rawNetworkMessage ? error?.message : i18n.t('actions.api.errors.status_401'),
          'error'
        );
        break;
      case 403:
        triggerToast(
          rawNetworkMessage ? error?.message : i18n.t('actions.api.errors.status_403'),
          'warning'
        );
        break;
      case 404:
        triggerToast(
          rawNetworkMessage ? error?.message : i18n.t('actions.api.errors.status_404'),
          'error'
        );
        break;
      case 422:
        triggerToast(
          rawNetworkMessage ? error?.message : i18n.t('actions.api.errors.status_422'),
          'error'
        );
        break;
      case 500:
        triggerToast(
          rawNetworkMessage ? error?.message : i18n.t('actions.api.errors.status_500'),
          'error'
        );
        break;
      default:
        triggerToast(
          isDevelopment ? `[DEV NETWORK] ${rawNetworkMessage}` : productionFallback,
          'error'
        );
    }
  }
};
